// Turns a term's registrations and invoices into the families listed on the Holiday Club page.

import {
  HolidayInvoice,
  HolidayRegistration,
  HolidayTerm,
  countDays,
  pendingInvoices,
} from "../../shared/holidayClub";

export type FamilyStatus = "attention" | "toInvoice" | "unpaid" | "paid" | "cancelled";

export interface FamilyChild {
  registrationId: string;
  name: string;
  days: HolidayRegistration["form"]["days"];
}

export interface Family {
  id: string;
  status: FamilyStatus;
  parentName: string;
  email: string;
  phone: string;
  children: FamilyChild[];
  fullDays: number;
  halfDays: number;
  discountReason: string;
  discountPercentage: number;
  discountPence: number | null;
  totalPence: number | null;
  registrationIds: string[];
  invoice?: HolidayInvoice;
  problem?: string;
}

export const STATUS_ORDER: FamilyStatus[] = ["attention", "toInvoice", "unpaid", "paid", "cancelled"];

export const STATUS_LABEL: Record<FamilyStatus, string> = {
  attention: "Needs attention",
  toInvoice: "To invoice",
  unpaid: "Unpaid",
  paid: "Paid",
  cancelled: "Cancelled",
};

export const STATUS_COLOR: Record<FamilyStatus, "warning" | "error" | "info" | "success" | "default"> = {
  attention: "warning",
  toInvoice: "error",
  unpaid: "info",
  paid: "success",
  cancelled: "default",
};

/** A family whose registrations are not on an invoice yet. */
const registrationFamily = (status: FamilyStatus, registrations: HolidayRegistration[], problem?: string): Family => {
  const first = registrations[0];
  const counts = registrations.map((r) => countDays(r.form.days));
  return {
    id: `${status}:${registrations.map((r) => r.id).join(",")}`,
    status,
    parentName: first.form.parentName,
    email: first.form.email,
    phone: first.form.phone,
    children: registrations.map((r) => ({ registrationId: r.id, name: r.form.childName, days: r.form.days })),
    fullDays: counts.reduce((sum, c) => sum + c.fullDays, 0),
    halfDays: counts.reduce((sum, c) => sum + c.halfDays, 0),
    discountReason: first.admin?.discountReason || "None",
    discountPercentage: first.admin?.discountPercentage ?? 0,
    discountPence: null,
    totalPence: null,
    registrationIds: registrations.map((r) => r.id),
    problem,
  };
};

export const buildFamilies = (term: HolidayTerm, registrations: HolidayRegistration[], invoices: HolidayInvoice[]) => {
  const phoneOf = new Map(registrations.map((r) => [r.id, r.form.phone]));
  const families: Family[] = invoices.map((invoice) => ({
    id: `invoice:${invoice.invoiceNo}`,
    status: invoice.cancelled ? "cancelled" : invoice.paid ? "paid" : "unpaid",
    parentName: invoice.parentName,
    email: invoice.email,
    phone: phoneOf.get(invoice.registrationIds[0]) ?? "",
    children: invoice.children,
    fullDays: invoice.fullDays,
    halfDays: invoice.halfDays,
    discountReason: invoice.discountReason,
    discountPercentage: invoice.discountPercentage,
    discountPence: invoice.discountPence,
    totalPence: invoice.totalPence,
    registrationIds: invoice.registrationIds,
    invoice,
  }));

  const pending = pendingInvoices(registrations, term);
  for (const content of pending.invoices) {
    families.push({
      ...registrationFamily("toInvoice", registrations.filter((r) => content.registrationIds.includes(r.id))),
      discountPence: content.discountPence,
      totalPence: content.totalPence,
    });
  }
  for (const problem of pending.problems) {
    families.push(registrationFamily("attention", registrations.filter((r) => problem.registrationIds.includes(r.id)), problem.message));
  }

  const cancelledByEmail = new Map<string, HolidayRegistration[]>();
  for (const r of registrations) {
    if (!r.admin?.cancelled || r.invoiceNo || r.removedFromForm) continue;
    const key = r.form.email.trim().toLowerCase();
    cancelledByEmail.set(key, [...(cancelledByEmail.get(key) ?? []), r]);
  }
  for (const group of cancelledByEmail.values()) families.push(registrationFamily("cancelled", group));

  // Work still to do first, then invoices newest first. Invoices keep number order whatever their status, so marking
  // one paid or cancelled doesn't move the list under the cursor.
  const rank = (family: Family) => (family.invoice ? 2 : family.status === "attention" ? 0 : family.status === "toInvoice" ? 1 : 3);
  families.sort((a, b) =>
    rank(a) - rank(b) ||
    (b.invoice?.invoiceNo ?? 0) - (a.invoice?.invoiceNo ?? 0) ||
    a.parentName.localeCompare(b.parentName));

  return { families, pendingCount: pending.invoices.length };
};

export const matchesSearch = (family: Family, search: string) => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [family.parentName, family.email, family.invoice ? `#${family.invoice.invoiceNo}` : "", ...family.children.map((c) => c.name)]
    .some((text) => text.toLowerCase().includes(needle));
};
